import { useLocation } from 'js/lib/useRouter';

import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import {
  useIsAuditing,
  useIsEnrolled,
  useIsEnrolledOutsideOfProgram,
  useUserProductInfoLoading,
} from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseUserProductInfoContext';
import { useGuidedCourseNextSteps } from 'bundles/unified-description-page-common/hooks/useGuidedCourseNextSteps';
import { useEnterpriseAdminEnrollment } from 'bundles/unified-description-page-common/providers/enterprise/EnterpriseAdminEnrollmentDataProvider';
import { addExternalPlatformTracking } from 'bundles/unified-description-page-common/utils';

const useContinueWithCourse = () => {
  const isEnrolled = useIsEnrolled();
  const isLoading = useUserProductInfoLoading();
  const isEnrolledOutsideOfProgram = useIsEnrolledOutsideOfProgram();
  const isAuditing = useIsAuditing();
  const location = useLocation();
  const { deepLinkItemId } = location.query;
  const shouldShowGoToCourseButton = !!((isEnrolledOutsideOfProgram || isEnrolled) && !deepLinkItemId && !isAuditing);

  return { shouldShowGoToCourseButton, isLoading };
};

export const useAutoContinueWithCourse = (isExternalParam: string | undefined, courseSlug: string | undefined) => {
  const { shouldShowGoToCourseButton, isLoading: isContinueButtonLoading } = useContinueWithCourse();
  const { isProgramMember: isMember } = useProgramMembership();
  const isExternal = isExternalParam === 'true';
  const skipNextSteps = !isExternal || !isMember;
  let redirectUrl = null;
  // Ideally we should skip the next steps API call if we are not showing the continue button,
  // but involves a bit of delay to determine if we are showing the continue button or not, because it involves data from multiple sources
  // Also, it will involve adding useEffect hook to trigger next steps API call after we determine if we are showing the continue button or not
  // Which makes the it a CSR interaction, and we want to avoid that for faster redirect
  // So we are calling the next steps API always only for users coming from LMS/LXPs (isExternal === true)
  // Review discussion for more context - https://github.com/webedx-spark/web/pull/46684#discussion_r1906977748
  const { resourcePath, loading: isNextStepLoading } = useGuidedCourseNextSteps(courseSlug, skipNextSteps);

  const features = useEnterpriseProductFeatures();
  const isFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const { isAdminEnrollmentEnabled: isGlobalAutoEnrollmentEnabled } = useEnterpriseAdminEnrollment();
  const shouldAutoContinueWithCourse = !!(
    shouldShowGoToCourseButton &&
    (features?.enableOptimizedLearnerFlow || isGlobalAutoEnrollmentEnabled)
  );

  if (resourcePath) {
    redirectUrl = addExternalPlatformTracking(resourcePath);
  }

  return {
    resourcePath: isExternal && shouldAutoContinueWithCourse ? redirectUrl : '',
    isLoading: isContinueButtonLoading || isNextStepLoading || isFeaturesLoading,
  };
};

export default useContinueWithCourse;
